import { DeviceListStatusCoCo, StatusColor } from '../enums.js';
import { draftMsg, inUseMsg, notInUseMsg, publishedMsg, t } from '../i18n/index.js';
import { groupBy, sortArrayByProperty } from './arrayUtils.js';
import { toTimestamp } from './dateUtilsCoCo.js';
import { DEVICE_LIST_DAMAGE_INSURANCE_OPTIONS } from './deviceListFormUtilsCoCo.js';
import { FILTER_VALUE_SEPARATOR, getDeviceSelectionProductTypeLabel } from './deviceListTabsUtilsCoCo.js';
import { toDeviceSelectionProductType } from './deviceSelectionListUtilsCoCo.js';
import { matchesSearch } from './productListUtilsCoCo.js';
import type { EmployeeProductListResponse } from '../../generated/api/employeeProductListResponse.js';
import type { DeviceSelectionProductTypeCoCo } from '../enums.js';

// Shown in the device list and device selection views when a value is missing
export const EMPTY_VALUE = '—';

export const getInUseText = (inUse?: boolean) => (inUse ? t.V34H(inUseMsg) : t.MUF5(notInUseMsg));

export const getDamageInsuranceText = (damageInsuranceOptions: string[] = []) => {
  const labels = DEVICE_LIST_DAMAGE_INSURANCE_OPTIONS.filter(({ value }) => damageInsuranceOptions.includes(value)).map(
    ({ label }) => label
  );
  return labels.length > 0 ? labels.join(', ') : t.MUF5(notInUseMsg);
};

// Column keys are the list item properties, so the list can be sorted by them
export enum DeviceListColumnCoCo {
  NAME = 'name',
  PRODUCT_TYPE = 'productType',
  CONTRACT_PERIOD = 'contractPeriod',
  DEVICE_SELECTION = 'deviceSelection',
  LAST_MODIFIED = 'lastModified',
  STATUS = 'status',
  COMPANY = 'company',
}

export interface DeviceListItemCoCo {
  uuid?: string;
  mdmId?: string;
  name: string;
  productType: DeviceSelectionProductTypeCoCo;
  contractTerms: string[];
  contractPeriod: string;
  deviceSelection: string;
  deviceSelectionUuid?: string;
  lastModified?: number;
  status: string;
  company: string;
}

export const getDeviceListStatusLabel = (status: string) => {
  switch (status) {
    case DeviceListStatusCoCo.PUBLISHED:
      return t.JN25(publishedMsg);
    case DeviceListStatusCoCo.DRAFT:
      return t.ZJQG(draftMsg);
    default:
      return status;
  }
};

export const getDeviceListStatusColor = (status?: string) => {
  switch (status) {
    case DeviceListStatusCoCo.PUBLISHED:
      return StatusColor.GREEN;
    case DeviceListStatusCoCo.DRAFT:
      return StatusColor.ORANGE;
    default:
      return undefined;
  }
};

// Deprecated lists are already left out by the backend, they are filtered also here to be sure they're never shown
export const isShownDeviceList = ({ status }: EmployeeProductListResponse) =>
  status !== DeviceListStatusCoCo.DEPRECATED;

export const toDeviceListItem = (
  list: EmployeeProductListResponse,
  deviceSelectionNames: Map<string, string>,
  getCompanyName: (mdmId?: string) => string
): DeviceListItemCoCo => {
  const contractTerms = list.contractTerms ?? [];
  return {
    uuid: list.uuid,
    mdmId: list.accountMdmId,
    name: list.name ?? '',
    productType: toDeviceSelectionProductType(list.productType),
    contractTerms,
    contractPeriod: contractTerms.join(', '),
    deviceSelection:
      (list.employeeProductSelectionUuid && deviceSelectionNames.get(list.employeeProductSelectionUuid)) || '',
    deviceSelectionUuid: list.employeeProductSelectionUuid,
    lastModified: toTimestamp(list.lastModifiedDate),
    status: list.status ?? '',
    company: getCompanyName(list.accountMdmId),
  };
};

export const splitFilterValues = (filter?: string) => filter?.split(FILTER_VALUE_SEPARATOR).filter(Boolean) ?? [];

export const filterDeviceListItems = (
  items: DeviceListItemCoCo[],
  filters: { search?: string; productType?: string; contractPeriod?: string; status?: string }
) => {
  const productTypes = splitFilterValues(filters.productType);
  const contractPeriods = splitFilterValues(filters.contractPeriod);
  const statuses = splitFilterValues(filters.status);
  return items.filter(
    item =>
      matchesSearch(filters.search ?? '', [
        item.name,
        item.deviceSelection,
        item.company,
        getDeviceSelectionProductTypeLabel(item.productType),
      ]) &&
      (productTypes.length === 0 || productTypes.includes(item.productType)) &&
      (contractPeriods.length === 0 || item.contractTerms.some(term => contractPeriods.includes(term))) &&
      (statuses.length === 0 || statuses.includes(item.status))
  );
};

// Without a sort param the most recently modified lists are shown first
export const sortDeviceListItems = (items: DeviceListItemCoCo[], sort?: string, order?: string) =>
  sort
    ? sortArrayByProperty([...items], sort, order)
    : sortArrayByProperty([...items], DeviceListColumnCoCo.LAST_MODIFIED, 'desc');

// Value and the number of its occurrences, e.g. [['Draft', 2], ['Published', 1]]
export const countValues = (values: string[]) =>
  Object.entries(groupBy(values, value => value)).map(([value, group]): [string, number] => [value, group.length]);
